import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFormAnalytics, type FormAnalytics as FormAnalyticsType } from '../api/client';
import FormAnalytics from '../components/forms/FormAnalytics';
import { formatDurationMs, formatPercent } from '../utils/formatters';
import { ChevronDown } from 'lucide-react';

export default function FormsPage() {
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { data: forms = [], isLoading } = useQuery({
    queryKey: ['form-analytics'],
    queryFn: () => getFormAnalytics(),
  });

  const selectedForm = forms.find((f) => f.formId === selectedFormId) || forms[0] || null;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Form Analytics</h1>

        {/* Form Selector */}
        {forms.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="btn-secondary gap-2"
            >
              {selectedForm?.formName || 'Select form'}
              <ChevronDown className="h-4 w-4" />
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 top-full z-10 mt-1 w-64 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                {(forms || []).map((form) => (
                  <button
                    key={form.formId || form.formName}
                    onClick={() => {
                      setSelectedFormId(form.formId);
                      setDropdownOpen(false);
                    }}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <p className="font-medium">{form.formName || 'Unknown Form'}</p>
                    <p className="text-xs text-gray-500">{form.url || ''}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {forms.length === 0 ? (
        <div className="card flex h-64 items-center justify-center">
          <div className="text-center">
            <p className="text-lg font-medium text-gray-500">No form data available</p>
            <p className="mt-1 text-sm text-gray-400">
              Form analytics will appear once forms are detected on your site
            </p>
          </div>
        </div>
      ) : selectedForm ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="card">
              <p className="text-sm text-gray-500">Total Starts</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {(selectedForm.totalStarts || 0).toLocaleString()}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">Submissions</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {(selectedForm.totalSubmissions || 0).toLocaleString()}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">Completion Rate</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {formatPercent(selectedForm.completionRate || 0)}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">Avg. Completion Time</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {formatDurationMs(selectedForm.avgCompletionTime || 0)}
              </p>
            </div>
          </div>

          {/* Field Analytics */}
          <FormAnalytics form={selectedForm} />
        </>
      ) : null}
    </div>
  );
}
